import React, { useEffect, useState } from "react";
import { Trophy, Medal, Star, Flame, Award } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import api from "@/services/api";
import ShimmerLoader from "../common/ShimmerLoader";

const Achievements = () => {
  const { user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [earned, setEarned] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch achievements from backend
  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const availableRes = await api.get("/achievements/available");
        const earnedRes = await api.get("/achievements");

        const availableList =
          availableRes?.data?.data?.achievements ||
          availableRes?.data?.achievements ||
          [];
        const earnedList =
          earnedRes?.data?.data?.achievements ||
          earnedRes?.data?.achievements ||
          [];

        setAvailable(availableList);
        setEarned(earnedList);
      } catch (error) {
        console.error("Error fetching achievements:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAchievements();
  }, []);

  // Extract unlocked badge types for easy comparison
  const earnedTypes = Array.isArray(earned)
    ? earned.map((a) => a.badge_type)
    : [];

  // Split into unlocked & locked lists
  const unlocked = available.filter((a) => earnedTypes.includes(a.badge_type));
  const locked = available.filter((a) => !earnedTypes.includes(a.badge_type));

  const progressPercentage = available.length
    ? (unlocked.length / available.length) * 100
    : 0;

  // Icon selector
  const getIcon = (symbol) => {
    switch (symbol) {
      case "🚴":
        return <Trophy className="w-5 h-5" />;
      case "🌅":
      case "⚡":
        return <Flame className="w-5 h-5" />;
      case "🗺️":
      case "🥉":
        return <Medal className="w-5 h-5" />;
      case "🧭":
      case "🥈":
        return <Award className="w-5 h-5" />;
      case "🥇":
      case "👑":
        return <Star className="w-5 h-5" />;
      default:
        return <Award className="w-5 h-5" />;
    }
  };

  // Shimmer loader
  if (loading) {
    return (
      <section className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Achievements
          </h2>
        </div>
        <ShimmerLoader count={6} />
      </section>
    );
  }

  return (
    <section className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Achievements
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Track your milestones and unlock new badges as you ride.
        </p>
      </div>

      {/* ✅ Progress Summary - Moved to Top */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex justify-between items-end mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">
              Project Progress
            </h3>{" "}
            {/* Using "Project Progress" label from inspiration or keep generic? Sticking to generic context but styled like inspiration. */}
            {/* Actually, user asked to move progress bar to top. Let's call it "Achievement Progress" */}
          </div>
          <div className="text-right">
            <span className="text-4xl font-bold text-gray-900 dark:text-white">
              {Math.round(progressPercentage)}%
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Completed
            </p>
          </div>
        </div>

        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-[#1e8a3b] to-[#114a1f] rounded-full shadow-sm" // Glossy gradient
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>{unlocked.length} badges unlocked</span>
          <span>{available.length} total available</span>
        </div>
      </div>

      {/* Unlocked Achievements */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Unlocked ({unlocked.length})
          </h3>
          <span className="text-xs text-green-600 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-full">
            Completed
          </span>
        </div>

        {unlocked.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {unlocked.map((a, index) => (
              <motion.div
                key={a.badge_type || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-[#1e8a3b] to-[#114a1f] dark:from-[#166534] dark:to-[#052e16] text-white p-6 rounded-3xl shadow-lg shadow-green-900/20 border border-white/10 flex flex-col justify-between min-h-[180px] hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
              >
                <div className="flex flex-col gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/90 text-green-800 shadow-sm flex items-center justify-center font-bold">
                    {getIcon(a.badge_icon)}
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-sm mb-1">
                      {a.badge_name}
                    </h4>
                    <p className="text-xs text-green-50/90 line-clamp-2">
                      {a.description}
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                  <span className="text-[10px] text-white/80 font-bold uppercase tracking-wider">
                    Unlocked
                  </span>
                  {/* Maybe a date here if available? */}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
            <p className="text-gray-500">
              You haven’t unlocked any achievements yet. Start riding!
            </p>
          </div>
        )}
      </div>

      {/* 🔒 Locked Achievements */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Locked ({locked.length})
          </h3>
          <span className="text-xs text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
            Pending
          </span>
        </div>

        {locked.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {locked.map((a, index) => (
              <motion.div
                key={a.badge_type || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-between min-h-[180px] opacity-70 hover:opacity-100 hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
              >
                <div className="flex flex-col gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 flex items-center justify-center grayscale">
                    {getIcon(a.badge_icon)}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 dark:text-gray-300 text-sm mb-1">
                      {a.badge_name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {a.description}
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-50 dark:border-gray-700 flex justify-between items-center">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Locked
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center bg-green-50 dark:bg-green-900/10 rounded-2xl">
            <p className="text-green-700 dark:text-green-400 font-medium">
              🎉 You’ve unlocked all achievements!
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Achievements;
